import PropTypes from 'prop-types';
import { useState, useCallback, useMemo } from 'react';
import _ from 'lodash';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { styled, alpha } from '@mui/material/styles';
import {
  Switch,
  FormControlLabel,
  FormGroup,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Tooltip,
  Box,
  Divider,
} from '@mui/material';
import { useSelector } from '../../../redux/store';
import { useTabs } from '../../../context/TabContext';
import Dnd from '../../DragAndDrop/Dnd';

CustomSettings.propTypes = {
  columnsState: PropTypes.object,
  setColumnsState: PropTypes.func,
  resetGridState: PropTypes.func,
  gridColumns: PropTypes.array,
  permanentHiddenColumns: PropTypes.array,
  handleGridSettingsUpdate: PropTypes.func,
};

export default function CustomSettings({
  columnsState,
  setColumnsState,
  gridColumns,
  resetGridState,
  handleGridSettingsUpdate,
  permanentHiddenColumns = [],
}) {
  const { addNewTabAndNavigate } = useTabs();

  const [anchorElement, setAnchorElement] = useState(null);
  const [isDraggedOver, setIsDraggedOver] = useState({});
  const { user } = useSelector((state) => state.user);
  const { settings } = user;

  const gridPersists = settings?.find((setting) => setting?.areaId === 'GridPersists')?.show ?? true;

  const open = Boolean(anchorElement);
  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorElement(event.currentTarget);
  };
  const handleClose = (e) => {
    setAnchorElement(null);
  };

  const saveToStorage = (currentState) => {
    if (gridPersists) {
      handleGridSettingsUpdate(currentState)
    }
  };

  const dropItem = useCallback(_.debounce((sourceItem, destinationItem) => {
    setIsDraggedOver({});
    setColumnsState(prevState => {
      const sourceItemIndex = prevState?.columns?.orderedFields?.findIndex(item => item === sourceItem?.field);
      const destinationItemIndex = prevState?.columns?.orderedFields?.findIndex(item => item === destinationItem?.field);

      const orderedFields = prevState?.columns?.orderedFields || [];
      const getCol = (field) => gridColumns.find(col => col.field === field);
      const after = getCol(orderedFields[destinationItemIndex - 1]);
      const before = getCol(orderedFields[destinationItemIndex]);
      // if col.disableReorder is true for the source destination, reordering is not allowed
      if ((before && before.disableReorder) || (after && after.disableReorder) || getCol(destinationItem.field).disableReorder) {
        return prevState; // Abort move
      }
      if (sourceItemIndex === -1 || destinationItemIndex === -1) return prevState;
      if (sourceItemIndex < destinationItemIndex) {
        for (let i = sourceItemIndex + 1; i <= destinationItemIndex; i++) {
          prevState.columns.orderedFields[i - 1] = prevState.columns.orderedFields[i];
        }
        prevState.columns.orderedFields[destinationItemIndex - 1] = sourceItem.field;
      }
      else {
        for (let i = sourceItemIndex; i > destinationItemIndex; i--) {
          prevState.columns.orderedFields[i] = prevState.columns.orderedFields[i - 1];
        }
        prevState.columns.orderedFields[destinationItemIndex] = sourceItem.field;
      }
      saveToStorage({ ...prevState });
      return { ...prevState };
    });
  }, 5), []);

  const switchCol = (field, checked) => {
    const hiddenCols = Array.isArray(permanentHiddenColumns) ? permanentHiddenColumns : [];
    if (hiddenCols.includes(field)) return;

    setColumnsState((prevState) => {
      const newColumns = { ...prevState?.columns };
      newColumns.columnVisibilityModel[field] = checked;
      saveToStorage({
        ...columnsState,
        columns: newColumns,
      });

      return { ...prevState, columns: newColumns };
    });
  };

  const orderedFieldsActionsLast = useMemo(() => {
    let orderedFields = Array.isArray(columnsState?.columns?.orderedFields) ? columnsState.columns.orderedFields : [];
    orderedFields = orderedFields.filter((field) => !permanentHiddenColumns.includes(field));

    if (!orderedFields.includes('actions')) return orderedFields;
    return [...orderedFields.filter((f) => f !== 'actions'), 'actions'];
  }, [columnsState]);
  
  return (
    <>
      <Tooltip title="Grid Settings" arrow placement="top">
        <IconButton color="info" sx={{ pb: 0.2, pl: 0.2 }} onClick={handleClick}>
          <SettingsIcon />
        </IconButton>
      </Tooltip>

      <StyledMenu id="basic-menu" anchorEl={anchorElement} open={open} onClose={handleClose}>
        {columnsState && (
          <FormGroup>
            {orderedFieldsActionsLast.map((field) => {
              const col = gridColumns.find((col) => col?.field === field);
              return (
                col && (
                  <Dnd key={field} item={col} itemId="field" onDrop={dropItem} disabled={col.disableReorder}>
                    <Box
                      onDragOver={() => setIsDraggedOver((prevState) => ({ ...prevState, [col.headerName]: true }))}
                      onDragLeave={() => setIsDraggedOver((prevState) => ({ ...prevState, [col.headerName]: false }))}
                    >
                      <Divider
                        sx={{
                          opacity: isDraggedOver[col.headerName] ? 1 : 0,
                          backgroundColor: '#b128bc',
                          height: '1.5px',
                          border: 'none',
                          margin: 0,
                        }}
                      />
                      <FormControlLabel
                        labelPlacement="start"
                        control={
                          <Switch
                            size="small"
                            checked={columnsState?.columns?.columnVisibilityModel[field] !== false}
                            onChange={(e) => {
                              e.stopPropagation(); // Prevents the switch from toggling when the row is dragged
                              switchCol(field, e.target.checked);
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              py: 0.5,
                              opacity: col.disableReorder ? 0.5 : 1,
                            }}
                          >
                            <DragIndicatorIcon fontSize="small" />
                            <Typography variant="body1"> {col?.settingsHeaderName || col?.headerName}</Typography>
                          </Box>
                        }
                        sx={{
                          m: 0,
                          width: 1,
                          justifyContent: 'space-between',
                          cursor: col.disableReorder ? 'default' : 'move', // Indicates this element is draggable
                        }}
                      />
                    </Box>
                  </Dnd>
                )
              );
            })}
          </FormGroup>
        )}
        <Divider
          sx={{
            mt: 1,
            mb: 1,
          }}
        />

        <MenuItem
          onClick={() => {
            resetGridState();
            handleClose();
          }}
        >
          <SettingsBackupRestoreIcon
            sx={{
              mr: 1.5,
            }}
          />
          <Typography
            variant="body1"
            sx={{
              mr: 1.5,
            }}
          >
            Reset Grid
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            addNewTabAndNavigate(`/dashboard/home/user/${user.id}?tab=settings`);
          }}
        >
          <DisplaySettingsIcon
            sx={{
              mr: 1.5,
            }}
          />
          <Typography variant="body1" sx={{}}>
            Grid Settings
            <OpenInNewIcon
              sx={{
                mr: 1.5,
              }}
            />
          </Typography>
        </MenuItem>
      </StyledMenu>
    </>
  );
}
const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    minWidth: 150,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    borderRadius: 6,
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
    backgroundColor: '#fff',
    border: 'solid 2px #c1c9d0',
    overflow: 'auto',
    '& .MuiMenuItem-root': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));
